import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { FormArray, FormGroup } from '@angular/forms';
import { Title, Meta, MetaDefinition } from '@angular/platform-browser';
import { AppToolsGstInvoiceBuilderService } from './app-tools-gst-invoice-builder.service';

@Component({
  selector: 'app-app-tools-gst-invoice-builder',
  templateUrl: './app-tools-gst-invoice-builder.component.html',
  styleUrls: ['./app-tools-gst-invoice-builder.component.scss'],
  standalone: false,
})
export class AppToolsGstInvoiceBuilderComponent implements OnInit {
  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef;
  uploadedFile: File | null = null; // Store the uploaded file temporarily
  uploadedLogoUrl: string | null = null; // URL to display the uploaded image
  isUploading: boolean = false; // Show loader during upload

  // List of Indian states and union territories
  states: string[] = [];
  rates: number[] = [];
  // Define HSN/SAC to GST rate mapping
  private gstRatesMap: { [prefix: string]: { gstRate: number; type: string } } =
    {};

  taxBreakdown = {
    totalTaxable: 0,
    cgst: 0,
    sgst: 0,
    igst: 0,
    totalAmount: 0,
  };

  invoiceForm: FormGroup;

  isInterstate: boolean = false; // Determines if it's an interstate transaction

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private titleService: Title,
    private metaService: Meta,
    private invoiceBuilderService: AppToolsGstInvoiceBuilderService
  ) {
    this.invoiceForm = this.invoiceBuilderService.initializeInvoiceForm();
    this.states = this.invoiceBuilderService.getStates();
    this.rates = this.invoiceBuilderService.getRates();
    this.gstRatesMap = this.invoiceBuilderService.getGstRatesMap();
    this.addItem();
  }

  ngOnInit(): void {
    this.setSEO();
    this.initializeGoogleTranslate();
    this.loadCustomerAndSupplierData();
    this.invoiceForm
      .get('supplierState')
      ?.valueChanges.subscribe(() => this.updateIsInterstate());
    this.invoiceForm
      .get('customerState')
      ?.valueChanges.subscribe(() => this.updateIsInterstate());

    // Trigger tax calculation when items change
    this.items.controls.forEach((itemControl) =>
      this.setupItemChangeListener(itemControl as FormGroup)
    );
  }

  // Consolidated listener for hsnSac, price, and quantity changes
  private setupItemChangeListener(itemControl: FormGroup): void {
    // Listen for changes in HSN/SAC, price, and quantity fields
    itemControl.valueChanges.subscribe((itemValue) => {
      const { hsnSac, price, quantity } = itemValue;

      // If HSN/SAC code is valid, update GST rate and GST type hint
      if (hsnSac && hsnSac.length >= 4) {
        const gstInfo = this.getGstInfoFromHsnSac(hsnSac);
        itemControl.get('gst')?.setValue(gstInfo.gstRate, { emitEvent: false });
        // Store or display GST type as a hint below the HSN field if required
        itemControl
          .get('gstTypeHint')
          ?.setValue(gstInfo.type, { emitEvent: false });
      } else {
        // Clear GST rate and GST type hint if HSN/SAC code is invalid
        itemControl.get('gst')?.setValue(null, { emitEvent: false });
        itemControl.get('gstTypeHint')?.setValue('', { emitEvent: false });
      }

      // Recalculate totals whenever any relevant field changes
      this.updateTotalAmount();
    });
  }

  // Helper method to retrieve GST rate and type
  private getGstInfoFromHsnSac(hsnSac: string): {
    gstRate: number;
    type: string;
  } {
    const prefix4 = hsnSac.slice(0, 4); // First 4 digits
    const prefix2 = hsnSac.slice(0, 2); // First 2 digits

    const gstInfo = this.gstRatesMap[prefix4] ||
      this.gstRatesMap[prefix2] || { gstRate: 18, type: 'General Goods' };

    return gstInfo;
  }

  getGstChipClass(rate: number): string {
    return `gst-chip gst-chip-${rate.toString().replace('.', '_')}`;
  }

  updateIsInterstate(): void {
    const supplierState = this.invoiceForm.get('supplierState')?.value;
    const customerState = this.invoiceForm.get('customerState')?.value;
    this.isInterstate =
      supplierState && customerState && supplierState !== customerState;
    this.updateTotalAmount(); // Recalculate totals on state change
  }

  // SEO metadata setup
  setSEO() {
    this.titleService.setTitle('AI Powered GST Invoice Generator | Inforida');
    const tags: MetaDefinition[] = this.invoiceBuilderService.getSEOTags();
    this.metaService.addTags(tags);
    this.addStructuredData();
  }

  triggerFileUpload(): void {
    this.fileInput.nativeElement.click();
  }

  onFileUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.isUploading = true; // Show loader

      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.uploadedLogoUrl = e.target.result; // Set the image URL to display
        this.uploadedFile = input && input.files && input.files[0]; // Store the file temporarily
        this.isUploading = false; // Hide loader
      };
      reader.readAsDataURL(input.files[0]);
    }
  }

  addStructuredData() {
    if (isPlatformBrowser(this.platformId)) {
      const jsonLd = this.invoiceBuilderService.getJsonLD();
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }

  // Getter for items FormArray
  get items(): FormArray {
    return this.invoiceForm.get('items') as FormArray;
  }

  // Method to create a new item form group
  createItem(): FormGroup {
    return this.invoiceBuilderService.initializeItems();
  }

  // Method to add a new item to the FormArray
  addItem() {
    const itemGroup = this.createItem();
    this.items.push(itemGroup);

    this.setupItemChangeListener(itemGroup);
    this.updateTotalAmount();
  }

  // Method to remove an item from the FormArray by index
  removeItem(index: number) {
    this.items.removeAt(index);
    this.updateTotalAmount();
  }

  resetLogo(): void {
    this.uploadedFile = null;
    this.uploadedLogoUrl = null;
  }

  private updateTotalAmount(): void {
    const items = this.invoiceForm.value.items;
    const isInterstate = this.isInterstate;

    this.taxBreakdown = this.invoiceBuilderService.calculateTaxBreakdown(
      items,
      isInterstate
    );

    // Update form controls with the latest tax breakdown values
    this.invoiceForm.patchValue({
      totalAmount: this.taxBreakdown.totalAmount,
      cgst: this.taxBreakdown.cgst,
      sgst: this.taxBreakdown.sgst,
      igst: this.taxBreakdown.igst,
    });
  }

  // PDF generation method
  generatePDF(): void {
    const invoiceData = this.invoiceForm.value;
    const uploadedLogoUrl = this.uploadedLogoUrl || '';
    const isInterstate = this.isInterstate;
    console.log('generatePDF', isInterstate);
    this.invoiceBuilderService.generatePDF(
      invoiceData,
      uploadedLogoUrl,
      isInterstate
    );
  }

  onSubmit(): void {
    console.log('onSubmit');
    if (this.invoiceForm.valid) {
      this.saveCustomerAndSupplierData();
      this.generatePDF();
    } else {
      this.invoiceForm.markAllAsTouched(); // Show validation errors only after form is submitted
    }
  }

  resetForm(): void {
    this.items.clear();
    this.invoiceForm.reset();
    this.resetLogo();
  }

  // Method to save customer and supplier information to local storage
  saveCustomerAndSupplierData(): void {
    const customerData = {
      name: this.invoiceForm.value.customerName,
      gstin: this.invoiceForm.value.customerGstin,
      pan: this.invoiceForm.value.customerPan,
      address: this.invoiceForm.value.customerAddress,
      state: this.invoiceForm.value.customerState,
      termsAndConditions: this.invoiceForm.value.termsAndConditions,
    };

    const supplierData = {
      name: this.invoiceForm.value.supplierName,
      gstin: this.invoiceForm.value.supplierGstin,
      pan: this.invoiceForm.value.supplierPan,
      address: this.invoiceForm.value.supplierAddress,
      state: this.invoiceForm.value.supplierState,
    };
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('uploadedLogoUrl', this.uploadedLogoUrl || '');
      localStorage.setItem('customerData', JSON.stringify(customerData));
      localStorage.setItem('supplierData', JSON.stringify(supplierData));
    }
  }

  // Method to load customer and supplier information from local storage
  loadCustomerAndSupplierData(): void {
    if (isPlatformBrowser(this.platformId)) {
      const customerData = localStorage.getItem('customerData');
      const supplierData = localStorage.getItem('supplierData');
      this.uploadedLogoUrl = localStorage.getItem('uploadedLogoUrl') || '';

      if (customerData) {
        const parsedCustomerData = JSON.parse(customerData);
        this.invoiceForm.patchValue({
          customerName: parsedCustomerData.name,
          customerGstin: parsedCustomerData.gstin,
          customerPan: parsedCustomerData.pan,
          customerAddress: parsedCustomerData.address,
          customerState: parsedCustomerData.state,
          termsAndConditions: parsedCustomerData.termsAndConditions,
        });
      }

      if (supplierData) {
        const parsedSupplierData = JSON.parse(supplierData);
        this.invoiceForm.patchValue({
          supplierName: parsedSupplierData.name,
          supplierGstin: parsedSupplierData.gstin,
          supplierPan: parsedSupplierData.pan,
          supplierAddress: parsedSupplierData.address,
          supplierState: parsedSupplierData.state,
        });
      }
    }
  }

  // Initialize Google Translate to detect and apply language
  initializeGoogleTranslate() {
    if (isPlatformBrowser(this.platformId)) {
      const userLang = navigator.language || (navigator as any)['userLanguage'];
      if (userLang && userLang.startsWith('hi')) {
        this.setLanguage('hi');
      }
    } else {
      // Optional: set a default language if running on the server
      this.setLanguage('en'); // Fallback to English or your default language
    }
  }

  // Method to change Google Translate language programmatically
  setLanguage(lang: string) {
    if (isPlatformBrowser(this.platformId)) {
      const selectElem = document.querySelector(
        '.goog-te-combo'
      ) as HTMLSelectElement;
      if (selectElem) {
        selectElem.value = lang;
        selectElem.dispatchEvent(new Event('change'));
      }
    }
  }
}
